import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { WorkLinksPreview } from "@/components/build/WorkLinksPreview";
import { EmailConfirmModal } from "@/components/EmailConfirmModal";
import { useEmailGuard } from "@/hooks/useEmailGuard";
import {
  MapPin, Clock, AlertTriangle, RotateCcw, CheckCircle2, XCircle, PauseCircle,
  Camera, Aperture, Sun, Mic, Plane, Clapperboard, Loader2,
} from "lucide-react";

const EQUIPMENT_META = {
  camera_owned: { label: "Camera Owned", icon: Camera },
  lenses: { label: "Lenses", icon: Aperture },
  lighting: { label: "Lighting", icon: Sun },
  audio: { label: "Audio", icon: Mic },
  drone: { label: "Drone", icon: Plane },
  editing_setup: { label: "Editing Setup", icon: Clapperboard },
};

function QueueRow({ app, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`application-row-${app.id}`}
      className={`w-full text-left px-4 py-3 border-b border-hairline transition-colors ${active ? "bg-brand/10" : "hover:bg-surface2"}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-ink truncate">{app.name}</div>
          <div className="text-xs text-ink-soft truncate">{app.primary_role || "—"}</div>
        </div>
        {app.stale && (
          <span title={`Waiting ${app.days_waiting} days`} className="shrink-0 mt-0.5">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-faint">
        {app.city && <span className="inline-flex items-center gap-0.5"><MapPin className="h-2.5 w-2.5" />{app.city}</span>}
        {app.experience_years != null && <span>{app.experience_years}y exp</span>}
        <span className="inline-flex items-center gap-0.5"><Clock className="h-2.5 w-2.5" />{app.days_waiting ?? 0}d waiting</span>
      </div>
    </button>
  );
}

export default function ApplicationsQueue() {
  const [activeTab, setActiveTab] = useState("applications");
  const [items, setItems] = useState([]);
  const [declineReasons, setDeclineReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [showHold, setShowHold] = useState(false);
  const [holdNote, setHoldNote] = useState("");
  const declineReasonRef = useRef(null);
  const holdNoteRef = useRef(null);

  // Move focus into the reason control whenever its panel opens, so keyboard/screen-reader
  // users land directly on the field instead of the toggle button.
  useEffect(() => {
    if (showDecline) declineReasonRef.current?.focus();
  }, [showDecline]);
  useEffect(() => {
    if (showHold) holdNoteRef.current?.focus();
  }, [showHold]);

  // Freelancer Edit Profile Simplification (2026-09): "Role Change Requests" tab, next to
  // Applications, same two-pane pattern. Own state so the two queues never mix selections.
  const [rcRequests, setRcRequests] = useState([]);
  const [rcPendingCount, setRcPendingCount] = useState(0);
  const [rcLoading, setRcLoading] = useState(true);
  const [selectedRcId, setSelectedRcId] = useState(null);
  const [rcStaffNote, setRcStaffNote] = useState("");
  const [rcBusy, setRcBusy] = useState(false);
  const guard = useEmailGuard();

  const loadList = useCallback(() => {
    setLoading(true);
    return api.get("/v1/build/applications").then((r) => {
      setItems(r.data.items || []);
      setDeclineReasons(r.data.decline_reasons || []);
    }).catch(() => toast.error("Couldn't load the applications queue")).finally(() => setLoading(false));
  }, []);

  const loadRoleChangeRequests = useCallback(() => {
    setRcLoading(true);
    return api.get("/v1/build/role-change-requests").then((r) => {
      setRcRequests(r.data.items || []);
      setRcPendingCount(r.data.pending_count || 0);
    }).catch(() => toast.error("Couldn't load role change requests")).finally(() => setRcLoading(false));
  }, []);

  const loadDetail = useCallback((id) => {
    setDetailLoading(true);
    api.get(`/v1/build/applications/${id}`).then((r) => setDetail(r.data))
      .catch(() => toast.error("Couldn't load this application"))
      .finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => { loadList(); loadRoleChangeRequests(); }, [loadList, loadRoleChangeRequests]);
  useEffect(() => { if (selectedId) loadDetail(selectedId); else setDetail(null); }, [selectedId, loadDetail]);
  useEffect(() => { setRcStaffNote(""); }, [selectedRcId]);

  const selectedRc = rcRequests.find((r) => r.id === selectedRcId) || null;

  const decideRoleChange = async (decision) => {
    setRcBusy(true);
    try {
      await api.post(`/v1/build/role-change-requests/${selectedRcId}/${decision}`, { staff_note: rcStaffNote.trim() || undefined });
      toast.success(decision === "approve" ? "Role change approved" : "Role change declined");
      setSelectedRcId(null);
      loadRoleChangeRequests();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || `Couldn't ${decision} this request`);
    } finally { setRcBusy(false); }
  };

  const afterActionRemovesFromQueue = () => {
    setItems((prev) => prev.filter((i) => i.id !== selectedId));
    setSelectedId(null);
    setDetail(null);
    // Perf fix (2026-10): this action just moved a row out of Applications (and possibly
    // into Interviews) — refresh the sidebar badges immediately instead of the usual 60s wait.
    window.__njmRefreshBadges?.();
  };

  const doSelect = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v1/build/applications/${selectedId}/select`);
      // Email Send Guard (2026-02): the status write/removal-from-queue below ALWAYS
      // happens in this one call — only the Calendly invite EMAIL (always external) waits
      // for a human to confirm via the shared "This will email N people" modal.
      if (guard.intercept(r.data, (typedCount) =>
        api.post(`/v1/build/admin/emails/confirm-pending`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount })
          .then((cr) => toast.success(cr.data.email_sent ? "Calendly invite sent." : "Invite email did NOT send. Open Interviews to resend or copy the link."))
      )) {
        afterActionRemovesFromQueue();
        return;
      }
      toast.success(r.data.email_sent ? "Selected — Calendly invite sent." : "Selected — invite email did NOT send. Open Interviews to resend or copy the link.");
      afterActionRemovesFromQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't select this applicant");
    } finally { setBusy(false); }
  };

  const doDecline = async () => {
    if (!declineReason) { toast.error("Choose a decline reason first"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/v1/build/applications/${selectedId}/decline`, { decline_reason: declineReason });
      if (guard.intercept(r.data, (typedCount) =>
        api.post(`/v1/build/admin/emails/confirm-pending`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount })
          .then((cr) => toast.success(cr.data.email_sent ? "Declined — email sent, record retained." : "Declined — email did NOT send, record retained."))
      )) {
        setShowDecline(false); setDeclineReason("");
        afterActionRemovesFromQueue();
        return;
      }
      toast.success(r.data.email_sent ? "Declined — email sent, record retained." : "Declined — email did NOT send (blocked or failed), record retained.");
      setShowDecline(false); setDeclineReason("");
      afterActionRemovesFromQueue();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't decline this applicant");
    } finally { setBusy(false); }
  };

  const doHold = async () => {
    if (!holdNote.trim()) { toast.error("A note is required to hold"); return; }
    setBusy(true);
    try {
      await api.post(`/v1/build/applications/${selectedId}/hold`, { note: holdNote });
      toast.success("Held — note added.");
      setShowHold(false); setHoldNote("");
      loadDetail(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't hold this applicant");
    } finally { setBusy(false); }
  };

  const doRequestLinks = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/v1/build/applications/${selectedId}/request-work-links`);
      if (guard.intercept(r.data, (typedCount) =>
        api.post(`/v1/build/admin/emails/confirm-pending`, { confirmation_token: r.data.confirmation_token, confirmed_count: typedCount })
          .then((cr) => toast.success(cr.data.email_sent ? "Requested — held awaiting applicant response." : "Held, but the request email did NOT send."))
      )) {
        loadDetail(selectedId);
        return;
      }
      toast.success(r.data.email_sent ? "Requested — held awaiting applicant response." : "Held, but the request email did NOT send (blocked or failed).");
      loadDetail(selectedId);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.code || "Couldn't send that request");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="applications-queue-page">
      <div className="flex items-center gap-1 border-b border-hairline px-4 pt-3 shrink-0" data-testid="applications-queue-tabs">
        <button onClick={() => setActiveTab("applications")} data-testid="applications-tab-btn"
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "applications" ? "border-brand text-ink" : "border-transparent text-faint hover:text-ink-soft"}`}>
          New Applications
        </button>
        <button onClick={() => setActiveTab("role-changes")} data-testid="role-change-requests-tab-btn"
          className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${activeTab === "role-changes" ? "border-brand text-ink" : "border-transparent text-faint hover:text-ink-soft"}`}>
          Role Change Requests
          {rcPendingCount > 0 && (
            <span data-testid="role-change-requests-pending-badge" className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-sky-500 px-1 text-[10px] font-bold text-white">
              {rcPendingCount}
            </span>
          )}
        </button>
      </div>

      {activeTab === "applications" ? (
    <div className="flex flex-1 min-h-0">
      <div className="w-[340px] shrink-0 border-r border-hairline flex flex-col min-h-0">
        <div className="px-4 py-3.5 border-b border-hairline">
          <h1 className="font-editorial text-lg text-ink">Applications</h1>
          <p className="text-xs text-faint mt-0.5">{items.length} awaiting review, oldest first</p>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="applications-queue-list">
          {loading ? (
            <div className="p-6 text-center text-sm text-faint">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState title="No applications waiting" hint="New submissions from /build/apply will show up here." testId="applications-queue-empty" />
          ) : (
            items.map((app) => (
              <QueueRow key={app.id} app={app} active={app.id === selectedId} onClick={() => setSelectedId(app.id)} />
            ))
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {!selectedId ? (
          <EmptyState title="Select an application to review" testId="applications-detail-empty" />
        ) : detailLoading || !detail ? (
          <div className="p-8 text-center text-sm text-faint">Loading…</div>
        ) : (
          <div className="max-w-3xl mx-auto px-6 py-6 space-y-6" data-testid="applications-detail-pane">
            <div>
              <h2 className="font-editorial text-2xl text-ink" data-testid="application-detail-name">{detail.name}</h2>
              <div className="flex items-center gap-2 text-sm text-ink-soft mt-1 flex-wrap">
                <span>{detail.primary_role}</span>
                {detail.secondary_roles?.filter((r) => r !== detail.primary_role).length > 0 && (
                  <span className="text-faint">+ {detail.secondary_roles.filter((r) => r !== detail.primary_role).join(", ")}</span>
                )}
                {detail.city && <span className="inline-flex items-center gap-0.5"><MapPin className="h-3 w-3" />{detail.city}{detail.country ? `, ${detail.country}` : ""}</span>}
                {detail.experience_years != null && <span>{detail.experience_years}y experience</span>}
              </div>
              <div className="text-xs text-faint mt-1">Applied {detail.applied_on} — {detail.days_waiting} day{detail.days_waiting === 1 ? "" : "s"} waiting</div>
            </div>

            {detail.returning_applicant && (
              <div data-testid="application-returning-banner" className="rounded-md border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                <div className="font-semibold flex items-center gap-1.5"><RotateCcw className="h-3.5 w-3.5" />Returning applicant</div>
                {detail.returning_applicant.is_member ? (
                  <p className="mt-1">This person is already an NJM Build member{detail.returning_applicant.date ? ` (since ${detail.returning_applicant.date})` : ""}.</p>
                ) : (
                  <p className="mt-1">
                    A prior record exists: <strong>{detail.returning_applicant.onboarding_status || "—"}</strong>
                    {detail.returning_applicant.date ? ` on ${detail.returning_applicant.date}` : ""}
                    {detail.returning_applicant.decline_reason ? ` (${detail.returning_applicant.decline_reason})` : ""}.
                    {detail.returning_applicant.still_in_queue && (
                      <>
                        {" "}
                        <button
                          data-testid="application-returning-jump-btn"
                          onClick={() => setSelectedId(detail.returning_applicant.record_id)}
                          className="underline font-medium hover:text-sky-700"
                        >
                          Still sitting in this queue — jump to it →
                        </button>
                      </>
                    )}
                  </p>
                )}
              </div>
            )}

            {detail.no_work_links && (
              <div data-testid="application-no-work-links-banner" className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-center justify-between gap-3 flex-wrap">
                <span className="flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" />No work links submitted.</span>
                <button data-testid="application-request-work-links-btn" onClick={doRequestLinks} disabled={busy}
                  className="rounded-md border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                  Request work links
                </button>
              </div>
            )}

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Bio</div>
              <p className="text-sm text-ink whitespace-pre-wrap" data-testid="application-bio">{detail.bio || <span className="text-faint italic">Not provided.</span>}</p>
            </div>
            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Achievements</div>
              <p className="text-sm text-ink whitespace-pre-wrap" data-testid="application-achievements">{detail.achievements || <span className="text-faint italic">Not provided.</span>}</p>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Work</div>
              <WorkLinksPreview candidate={detail} />
            </div>

            {Object.keys(EQUIPMENT_META).some((k) => detail[k]) && (
              <div>
                <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Equipment</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(EQUIPMENT_META).filter(([k]) => detail[k]).map(([k, meta]) => {
                    const Icon = meta.icon;
                    return (
                      <div key={k} className="text-sm text-ink flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-faint" /><span className="text-faint">{meta.label}:</span> {detail[k]}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {detail.application_v2_answers && Object.keys(detail.application_v2_answers).length > 0 && (
              <div data-testid="application-v2-answers">
                <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Application Answers</div>
                <div className="space-y-1.5">
                  {Object.entries(detail.application_v2_answers).map(([k, v]) => (
                    <div key={k} className="text-sm text-ink" data-testid={`application-v2-answer-${k}`}>
                      <span className="text-faint">{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}:</span>{" "}
                      {Array.isArray(v) ? v.join(", ") : v}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Internal Notes</div>
              {detail.internal_notes ? (
                <div className="space-y-2" data-testid="application-internal-notes">
                  {detail.internal_notes.split("\n\n").filter(Boolean).map((note, i) => (
                    <div key={i} className="text-xs text-ink-soft bg-surface2 rounded-md px-3 py-2 whitespace-pre-wrap">{note}</div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-faint italic">No notes yet.</div>
              )}
            </div>

            {/* Sticky-panel visibility fix, pass 3 (2026-02, per Cursor review): pass 2 put
                panels + buttons in one sticky bottom-0 block, which fixed desktop but on short
                viewports (mobile 390x844, or any ~700px-tall window) a long panel could still
                push the action row above the top of the viewport, since a sticky block has no
                height cap. Each panel now has its own max-height + internal scroll, so the
                panel's own content scrolls while the action row directly below it is always
                within the capped total height and never pushed off-screen. */}
            <div className="sticky bottom-0 bg-paper pt-2 border-t border-hairline">
              {showDecline && (
                <div id="application-decline-panel" role="region" aria-label="Decline reason"
                  className="mb-3 max-h-[38vh] overflow-y-auto rounded-md border border-red-200 bg-red-50 p-4 space-y-3" data-testid="application-decline-panel">
                  <div className="text-xs font-medium text-red-900">Decline reason (required)</div>
                  <select ref={declineReasonRef} data-testid="application-decline-reason-select" value={declineReason} onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full rounded-md border border-red-300 bg-white px-3 py-2 text-sm text-ink">
                    <option value="">Select a reason…</option>
                    {declineReasons.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button data-testid="application-decline-confirm-btn" onClick={doDecline} disabled={busy || !declineReason}
                    className="rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                    Confirm Decline
                  </button>
                </div>
              )}
              {showHold && (
                <div id="application-hold-panel" role="region" aria-label="Hold note"
                  className="mb-3 max-h-[38vh] overflow-y-auto rounded-md border border-hairline bg-surface2 p-4 space-y-3" data-testid="application-hold-panel">
                  <div className="text-xs font-medium text-ink-soft">Note (required)</div>
                  <textarea ref={holdNoteRef} data-testid="application-hold-note-input" rows={2} value={holdNote} onChange={(e) => setHoldNote(e.target.value)}
                    className="w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" placeholder="Why is this on hold?" />
                  <button data-testid="application-hold-confirm-btn" onClick={doHold} disabled={busy || !holdNote.trim()}
                    className="rounded-md bg-ink px-4 py-2 text-sm font-semibold text-white hover:bg-ink/90 disabled:opacity-50">
                    Confirm Hold
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 py-3 flex-wrap">
                <button data-testid="application-select-btn" onClick={doSelect} disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50">
                  {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Select
                </button>
                <button data-testid="application-decline-open-btn" onClick={() => setShowDecline((v) => !v)} disabled={busy}
                  aria-expanded={showDecline} aria-controls="application-decline-panel"
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                  <XCircle className="h-3.5 w-3.5" /> Decline
                </button>
                <button data-testid="application-hold-open-btn" onClick={() => setShowHold((v) => !v)} disabled={busy}
                  aria-expanded={showHold} aria-controls="application-hold-panel"
                  className="inline-flex items-center gap-1.5 rounded-md border border-hairline px-4 py-2 text-sm font-medium text-ink-soft hover:bg-surface2 disabled:opacity-50">
                  <PauseCircle className="h-3.5 w-3.5" /> Hold
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
      ) : (
        <RoleChangeRequestsPanel
          items={rcRequests}
          loading={rcLoading}
          selectedId={selectedRcId}
          onSelect={setSelectedRcId}
          selected={selectedRc}
          staffNote={rcStaffNote}
          onStaffNoteChange={setRcStaffNote}
          busy={rcBusy}
          onApprove={() => decideRoleChange("approve")}
          onDecline={() => decideRoleChange("decline")}
        />
      )}
      <EmailConfirmModal open={guard.open} preview={guard.preview} busy={guard.busy} onConfirm={guard.confirm} onCancel={guard.cancel} />
    </div>
  );
}

function RoleChangeRequestsPanel({ items, loading, selectedId, onSelect, selected, staffNote, onStaffNoteChange, busy, onApprove, onDecline }) {
  return (
    <div className="flex flex-1 min-h-0">
      <div className="w-[340px] shrink-0 border-r border-hairline flex flex-col min-h-0">
        <div className="px-4 py-3.5 border-b border-hairline">
          <h1 className="font-editorial text-lg text-ink">Role Change Requests</h1>
          <p className="text-xs text-faint mt-0.5">{items.filter((i) => i.status === "pending").length} pending review</p>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="role-change-requests-list">
          {loading ? (
            <div className="p-6 text-center text-sm text-faint">Loading…</div>
          ) : items.length === 0 ? (
            <EmptyState title="No role change requests" hint="Freelancer role change requests will show up here." testId="role-change-requests-empty" />
          ) : (
            items.map((req) => (
              <button key={req.id} onClick={() => onSelect(req.id)} data-testid={`role-change-request-row-${req.id}`}
                className={`w-full text-left px-4 py-3 border-b border-hairline transition-colors ${req.id === selectedId ? "bg-brand/10" : "hover:bg-surface2"}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-ink truncate">{req.freelancer_name}</div>
                    <div className="text-xs text-ink-soft truncate">{req.current_primary || "—"} → {req.requested_primary}</div>
                  </div>
                  <span className={`shrink-0 mt-0.5 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${
                    req.status === "pending" ? "bg-amber-500/10 text-amber-600" : req.status === "approved" ? "bg-emerald-500/10 text-emerald-600" : "bg-red-500/10 text-red-600"
                  }`}>{req.status}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto min-h-0">
        {!selected ? (
          <EmptyState title="Select a request to review" testId="role-change-request-detail-empty" />
        ) : (
          <div className="max-w-2xl mx-auto px-6 py-6 space-y-5" data-testid="role-change-request-detail-pane">
            <div>
              <h2 className="font-editorial text-2xl text-ink" data-testid="role-change-request-freelancer-name">{selected.freelancer_name}</h2>
              <div className="text-xs text-faint mt-1">Requested {selected.created_at ? new Date(selected.created_at).toLocaleDateString() : ""}</div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-faint mb-1">Current</div>
                <div className="text-sm text-ink" data-testid="role-change-current-roles">
                  {[selected.current_primary, ...(selected.current_secondary || [])].filter(Boolean).join(", ") || "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-faint mb-1">Requested</div>
                <div className="text-sm text-ink font-medium" data-testid="role-change-requested-roles">
                  {[selected.requested_primary, ...(selected.requested_secondary || [])].filter(Boolean).join(", ") || "—"}
                </div>
              </div>
            </div>

            <div>
              <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Reason</div>
              <p className="text-sm text-ink whitespace-pre-wrap" data-testid="role-change-reason">{selected.reason}</p>
            </div>

            {selected.status !== "pending" ? (
              <div className="rounded-md border border-hairline bg-surface2 p-3 text-sm text-ink-soft" data-testid="role-change-decision-summary">
                {selected.status === "approved" ? "Approved" : "Declined"} by {selected.decided_by} on {selected.decided_at ? new Date(selected.decided_at).toLocaleDateString() : "—"}
                {selected.staff_note && <div className="mt-1 italic">"{selected.staff_note}"</div>}
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-medium uppercase tracking-wider text-faint mb-2">Staff note (optional)</div>
                  <textarea data-testid="role-change-staff-note-input" rows={2} value={staffNote} onChange={(e) => onStaffNoteChange(e.target.value)}
                    className="w-full rounded-md border border-hairline bg-paper px-3 py-2 text-sm text-ink" placeholder="Visible to the freelancer if you decline" />
                </div>
                <div className="flex items-center gap-2">
                  <button data-testid="role-change-approve-btn" onClick={onApprove} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-ink hover:bg-brand-dim disabled:opacity-50">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Approve
                  </button>
                  <button data-testid="role-change-decline-btn" onClick={onDecline} disabled={busy}
                    className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                    <XCircle className="h-3.5 w-3.5" /> Decline
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
